import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Typography } from '@otp/ui';
import { Routes } from 'src/components/common/router/constants';
import { ReactComponent as Mascot } from './mascot.svg';
import { BookPlayIcon } from '@otp/icons';
import { useLmsCurriculums } from 'src/hooks/query/lms/curriculums/use-curriculums';
import { LmsCurriculumUserStatus } from 'src/graphql/generated/graphql';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';

export const LearningCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, loading, totalCount } = useLmsCurriculums(
    {
      filter: {
        isArchived: false,
      },
      pagination: {
        page: 0,
        limit: 100,
      },
    },
    { isAdmin: false },
  );

  const courseStats = useMemo(() => {
    if (loading) {
      return {
        total: 0,
        inProgress: [],
        completed: [],
        available: [],
      };
    }
    return {
      total: totalCount || 0,
      inProgress:
        data?.filter(
          (c) =>
            c.curriculumUsers?.[0]?.status ===
            LmsCurriculumUserStatus.InProgress,
        ) || [],
      completed:
        data?.filter(
          (c) =>
            c.curriculumUsers?.[0]?.status ===
            LmsCurriculumUserStatus.Completed,
        ) || [],
      available:
        data?.filter(
          (c) =>
            c.curriculumUsers?.length === 0 ||
            c.curriculumUsers?.[0]?.status ===
              LmsCurriculumUserStatus.Assigned ||
            !c.curriculumUsers?.[0]?.status,
        ) || [],
    };
  }, [data, loading, totalCount]);

  const firstInProgressCourse = courseStats.inProgress?.[0];
  const firstAvailableCourse = courseStats.available?.[0];
  const courses = [
    firstInProgressCourse && {
      testId: 'learning-center-in-progress-card',
      badgeClass: 'bg-[#2777D4] text-white',
      titleKey: 'in-progress-card',
      course: firstInProgressCourse,
    },
    firstAvailableCourse && {
      testId: 'learning-center-available-course-card',
      badgeClass: 'bg-[#F8B92A] text-black',
      titleKey: 'available-card',
      course: firstAvailableCourse,
    },
  ];
  return (
    <Grid container spacing={3} className="h-full">
      <Grid item xs={12} md={7} className="h-full">
        <Card className="bg-[#F8B92A] p-6 h-full relative">
          <Grid container rowSpacing={3}>
            {/* HEADER */}
            <Grid item container justifyContent="space-between" xs={12}>
              <Grid container item xs={6} direction="column" gap={2}>
                <BookPlayIcon className="text-white w-10 h-10" />
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  fontFamily="Inter"
                >
                  {t('my-organisation.learning-center.card.title')}
                </Typography>
                <Typography
                  data-testid="learning-center-total-count"
                  variant="h4"
                  fontWeight="bold"
                  className="text-[32px]"
                >
                  {courseStats.total}
                </Typography>
              </Grid>
              <Grid container item xs={6} justifyContent="flex-end" gap={2}>
                <Mascot />
              </Grid>
            </Grid>

            {/* LIST */}
            <Grid item xs={12}>
              <Grid container rowSpacing={4}>
                {[
                  { id: 'in-progress', value: courseStats?.inProgress?.length },
                  { id: 'completed', value: courseStats?.completed?.length },
                  { id: 'available', value: courseStats?.available?.length },
                ].map(({ id, value }) => (
                  <Grid item xs={12} key={id}>
                    <div
                      className="flex justify-between"
                      data-testid={`learning-center-${id}-count`}
                    >
                      <Typography variant="body2">
                        {t(`my-organisation.learning-center.card.${id}`)}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {value}
                      </Typography>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* CTA */}
            <Grid item xs={12}>
              <Card
                className="bg-black text-white py-3 px-7 rounded-xl w-full h-14 flex items-center justify-between group"
                isLink
                onClick={() => navigate(Routes.lmsCurriculums.path)}
                linkIconClassName="text-white mr-2 mt-1.5 group-hover:text-black"
              >
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  className="text-white group-hover:text-black"
                >
                  {t('my-organisation.learning-center.card.cta')}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item xs={12} md={5} className="h-full">
        <Grid container rowSpacing={2}>
          {courses.filter(Boolean).map((item) => (
            <Grid item xs={12} key={item.testId}>
              <Grid
                container
                className="bg-gray-50 rounded-lg p-6"
                rowSpacing={2}
                data-testid={item.testId}
              >
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    className={`${item.badgeClass} py-1 px-3 rounded-full w-fit`}
                  >
                    {t(
                      `my-organisation.learning-center.card.${item.titleKey}.title`,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight="bold">
                    {item.course.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Card
                    className="bg-white border border-gray-200 py-3 px-4 rounded-lg w-full"
                    isLink
                    onClick={() => window.open(item.course.link, '_blank')}
                    linkIconClassName="absolute top-1/2 right-4 transform -translate-y-1/2 text-black"
                  >
                    <Typography fontWeight="medium" className="pr-8">
                      {t(
                        `my-organisation.learning-center.card.${item.titleKey}.cta`,
                      )}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LearningCenter;
